import React, { useState } from 'react';
import { useCMS } from '../context/CMSContext';
import { Handshake, Building2, Users, Heart } from 'lucide-react';

const Partnership = () => {
  const { content, getPageData, API_URL } = useCMS();
  const pageData = getPageData('/partnership');

  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState(null); // 'success', 'error', 'network_error'
  const [errorText, setErrorText] = useState('');

  const c = (key, fallback) => {
    if (pageData && pageData.sectionData) {
      const sections = ['header', 'models', 'cta'];
      for (const type of sections) {
        if (key.includes(`_${type}_`)) {
          const field = key.split(`_${type}_`)[1];
          const val = pageData.sectionData[type]?.[field];
          if (val) return val;
        }
      }
    }
    return content[key] || fallback;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!fullName || !email || !message) {
      alert("Please fill in all fields.");
      return;
    }

    setSubmitting(true);
    setStatus(null);
    setErrorText('');

    try {
      const response = await fetch(`${API_URL || 'http://127.0.0.1:5000/api'}/inquiries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          full_name: fullName,
          email: email,
          interest_area: 'Partnership',
          message: message
        })
      });

      if (response.ok) {
        setStatus('success');
        setFullName('');
        setEmail('');
        setMessage('');
      } else {
        const errorData = await response.json().catch(() => ({}));
        setErrorText(errorData.error || 'Server error occurred');
        setStatus('error');
      }
    } catch (err) {
      console.error(err);
      setStatus('network_error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="animate-in fade-in duration-1000 bg-white">
      
      {/* 1. Partnership Hero */}
      <section className="relative pt-40 pb-24 px-4 bg-white overflow-hidden border-b border-brandBeige/50">
        <div className="absolute top-0 right-0 w-1/2 h-full bg-brandBeige/30 skew-x-[-15deg] translate-x-32 hidden lg:block"></div>
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="max-w-2xl">
            <h1 className="text-5xl md:text-6xl font-black text-brandBlack mb-6" dangerouslySetInnerHTML={{__html: c('partnership_header_title', 'Working Together <br/><span class="text-brandGreen">for Impact</span>')}}>
            </h1>
            <p className="text-lg text-brandBlack/60 leading-relaxed font-medium">
              {c('partnership_header_subtitle', 'Partnerships are central to how we deliver sustainable, community-driven solutions. We work with aligned partners to co-create impact.')}
            </p>
          </div>
        </div>
      </section>

      {/* 2. Who We Partner With */}
      <section className="py-24 bg-brandBeige/10">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-black text-brandBlack">Who We Partner With</h2>
            <div className="h-1 w-16 bg-brandGreen mx-auto mt-4 rounded-full"></div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <PartnerCard 
              icon={<Handshake size={28} />} 
              title={c('partnership_models_model1_title', 'Development Partners')} 
              desc={c('partnership_models_model1_desc', 'To design, fund, and scale high impact programs.')} 
            />
            <PartnerCard 
              icon={<Building2 size={28} />} 
              title={c('partnership_models_model2_title', 'Government & Institutions')} 
              desc={c('partnership_models_model2_desc', 'To align initiatives with national and local development priorities.')} 
            />
            <PartnerCard 
              icon={<Users size={28} />} 
              title={c('partnership_models_model3_title', 'Community Organizations')} 
              desc={c('partnership_models_model3_desc', 'To ensure locally led, culturally relevant, and inclusive implementation.')} 
            />
            <PartnerCard 
              icon={<Building2 size={28} />} 
              title={c('partnership_models_model4_title', 'Private Sector Partners')} 
              desc={c('partnership_models_model4_desc', 'To drive innovation, market access, and sustainable financing models.')} 
            />
            <PartnerCard 
              icon={<Heart size={28} />} 
              title={c('partnership_models_model5_title', 'Religious Institutions')} 
              desc={c('partnership_models_model5_desc', 'To design programs that impact their members of their ministries.')} 
            />
          </div>
        </div>
      </section>

      {/* 3. Why Partner & Form */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col lg:flex-row gap-20 items-start">
            
            <div className="lg:w-1/2 space-y-10">
              <h2 className="text-4xl font-black text-brandBlack leading-tight">
                Why Partner With Eunikare?
              </h2>
              <div className="space-y-6">
                <BenefitItem text={c('partnership_cta_benefit1', 'Trusted local implementation partner')} />
                <BenefitItem text={c('partnership_cta_benefit2', 'Community driven and impact focused approach')} />
                <BenefitItem text={c('partnership_cta_benefit3', 'Transparent and accountable systems')} />
                <BenefitItem text={c('partnership_cta_benefit4', 'Flexible collaboration models')} />
              </div>
            </div>

            <div className="lg:w-1/2 w-full">
              <div className="bg-[#f7f6f2] p-10 rounded-[2rem]">
                <h3 className="text-2xl font-black mb-4">Become a Partner</h3>
                <p className="text-brandBlack/60 mb-8 text-sm font-medium">Collaborate with us to design and deliver solutions that create lasting social and economic impact.</p>
                
                {status === 'success' && (
                  <div className="text-center py-8 bg-white rounded-2xl p-6 border border-brandGreen/10 shadow-sm animate-in fade-in duration-500">
                    <h4 className="text-2xl font-bold text-brandGreen mb-3">Submission Successful!</h4>
                    <p className="text-brandBlack/70 text-sm mb-6 leading-relaxed">
                      Thank you for your interest! Your partnership inquiry has been successfully sent to <span className="font-bold text-brandGreen">info.eunikareinternational@gmail.com</span> and saved in our systems.
                    </p>
                    <button 
                      onClick={() => setStatus(null)} 
                      className="bg-brandBlack text-brandWhite px-8 py-3 rounded-full font-bold text-xs hover:bg-brandGreen transition-all active:scale-95"
                    >
                      Send Another Inquiry
                    </button>
                  </div>
                )}

                {status === 'error' && (
                  <div className="text-center py-8 bg-white rounded-2xl p-6 border border-red-200 shadow-sm animate-in fade-in duration-500">
                    <h4 className="text-2xl font-bold text-red-500 mb-3">Transmission Failed</h4>
                    <p className="text-brandBlack/70 text-sm mb-6 leading-relaxed">
                      We encountered an error trying to process your inquiry:
                      <br/>
                      <span className="font-mono text-xs text-red-500">{errorText || 'Internal Server Error'}</span>
                    </p>
                    <button 
                      onClick={() => setStatus(null)} 
                      className="bg-red-500 text-white px-8 py-3 rounded-full font-bold text-xs hover:bg-red-600 transition-all active:scale-95"
                    >
                      Try Again
                    </button>
                  </div>
                )}

                {status === 'network_error' && (
                  <div className="text-center py-8 bg-white rounded-2xl p-6 border border-yellow-300 shadow-sm animate-in fade-in duration-500">
                    <h4 className="text-2xl font-bold text-yellow-600 mb-3">Network Issue</h4>
                    <p className="text-brandBlack/70 text-sm mb-6 leading-relaxed">
                      A network issue occurred. Please check that the backend server is running and try again.
                    </p>
                    <button 
                      onClick={() => setStatus(null)} 
                      className="bg-brandBlack text-brandWhite px-8 py-3 rounded-full font-bold text-xs hover:bg-brandGreen transition-all active:scale-95"
                    >
                      Try Again
                    </button>
                  </div>
                )}

                {status === null && (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <input 
                      type="text" 
                      placeholder="Full Name" 
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      required
                      className="w-full bg-white rounded-xl p-4 outline-none focus:ring-1 focus:ring-brandGreen text-sm" 
                    />
                    <input 
                      type="email" 
                      placeholder="Organization Email" 
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="w-full bg-white rounded-xl p-4 outline-none focus:ring-1 focus:ring-brandGreen text-sm" 
                    />
                    <textarea 
                      rows="4" 
                      placeholder="How would you like to partner?" 
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      required
                      className="w-full bg-white rounded-xl p-4 outline-none focus:ring-1 focus:ring-brandGreen text-sm"
                    ></textarea>
                    <button 
                      type="submit" 
                      disabled={submitting}
                      className="w-full bg-[#1b9a55] text-white py-4 rounded-xl font-bold hover:bg-[#147a42] transition-colors shadow-md active:scale-[0.98] disabled:bg-gray-400 disabled:scale-100"
                    >
                      {submitting ? 'Sending inquiry...' : 'Send Inquiry'}
                    </button>
                    <p className="text-[9px] text-center text-brandBlack/50 font-bold uppercase tracking-widest pt-2">
                      Partnership models include program delivery, co-funding, and strategic collaboration.
                    </p>
                  </form>
                )}
              </div>
            </div>

          </div>
        </div>
      </section>

    </div>
  );
};

const PartnerCard = ({ icon, title, desc }) => (
  <div className="bg-white p-8 rounded-3xl shadow-sm hover:shadow-md transition-all">
    <div className="text-[#1b9a55] mb-6">{icon}</div>
    <h3 className="text-xl font-black text-brandBlack mb-3">{title}</h3>
    <p className="text-brandBlack/60 text-sm leading-relaxed">{desc}</p>
  </div>
);

const BenefitItem = ({ text }) => (
  <div className="flex items-center gap-4">
    <div className="w-6 h-6 bg-[#1b9a55]/20 rounded-full flex items-center justify-center">
      <div className="w-2 h-2 bg-[#1b9a55] rounded-full"></div>
    </div>
    <span className="text-brandBlack font-bold">{text}</span>
  </div>
);

export default Partnership;
